import { useState } from 'react';
import {
  LINE_SOURCE_LABELS,
  ORDER_STATUSES,
  refundLineStatusLabel,
  type LedgerOrderItem,
  type LedgerOrderLine,
} from '@cuito/types';
import { Button, centsFromYuan, formatBeijingTime, formatYuan, orderNoShort } from '@cuito/ui';
import { updateOrderNotes } from '../../cashier/api/orders';
import { ORDER_STATUS_TEXT } from '../../cashier/components/OrderCard';
import { readServerMessage } from '../../lib/apiError';
import { AMOUNT_UNAVAILABLE_TEXT, PRICE_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';
import { refundStatusClass, refundStatusLabel } from './refundLabels';

/** 备注上限（契约 §12.2 第 11 行；UI 规范 §3.2 的逐字文案「备注最多 100 个字符」） */
const NOTE_MAX_LENGTH = 100;

/**
 * 订单状态词表**复用**收银端的 `ORDER_STATUS_TEXT`（`cashier/components/OrderCard.tsx:26-34`）：
 * 同一张订单在收银端与账本页必须说同一句话，再抄一份就会漂移。
 */
function orderStatusLabel(status: string): string {
  // `ORDER_STATUSES` 是 `as const` 元组 ⇒ `find` 已经收窄成 `OrderStatus | undefined`，不需要 cast
  const known = ORDER_STATUSES.find((value) => value === status);
  return known === undefined ? status : ORDER_STATUS_TEXT[known];
}

/** 划价的判据：**按分位整数比较**（`'60'` 与 `'60.00'` 的字符串比较会误判成「有优惠」） */
function isDiscounted(line: LedgerOrderLine): boolean {
  const original = centsFromYuan(line.originalPrice);
  const unit = centsFromYuan(line.unitPrice);
  if (original === null || unit === null) return false;
  return original !== unit;
}

function OrderGroup({
  order,
  onRefund,
  onNotesSaved,
}: {
  order: LedgerOrderItem;
  onRefund?: (order: LedgerOrderItem) => void;
  onNotesSaved?: (orderId: string, notes: string) => void;
}) {
  const [notes, setNotes] = useState(order.notes);
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteNotice, setNoteNotice] = useState<string | null>(null);

  const total = formatServerYuan(order.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT;
  const refunded = formatServerYuan(order.refundedAmount) ?? AMOUNT_UNAVAILABLE_TEXT;

  const saveNotes = async (): Promise<void> => {
    if (notes.length > NOTE_MAX_LENGTH) {
      setNoteNotice(null);
      setNoteError('备注最多 100 个字符');
      return;
    }
    setSaving(true);
    setNoteError(null);
    setNoteNotice(null);
    try {
      const saved = await updateOrderNotes(order.orderId, notes);
      setNotes(saved.notes);
      setNoteNotice('备注已保存');
      onNotesSaved?.(order.orderId, saved.notes);
    } catch (e) {
      setNoteError(readServerMessage(e) ?? '保存备注失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mb-4 rounded-lg border-2 border-black"
      data-testid="ledger-order-row"
      data-order-no={order.orderNo}
    >
      <header className="flex flex-wrap items-center gap-3 border-b-2 border-black bg-gray-50 px-4 py-3">
        <span className="text-base font-bold">订单 {orderNoShort(order.orderNo)}</span>
        <span className="text-sm text-gray-500">短码 {order.shortCode}</span>
        <span className="text-sm text-gray-500">{order.locationName ?? '-'}</span>
        <span className="text-sm text-gray-500">{formatBeijingTime(order.createdAt)}</span>
        <span
          className={`rounded border px-1.5 py-0.5 text-xs font-bold ${refundStatusClass(order.refundStatus)}`}
          data-testid="ledger-order-refund-status"
        >
          {refundStatusLabel(order.refundStatus)}
        </span>
        <span className="text-sm text-gray-500" data-testid="ledger-order-status">
          {orderStatusLabel(order.status)}
        </span>
        <span className="ml-auto text-base font-bold" data-testid="ledger-order-total">
          {total}
        </span>
        <span className="text-sm text-gray-500" data-testid="ledger-order-refunded">
          已退 {refunded}
        </span>
        {onRefund && (
          <Button
            size="sm"
            variant="danger"
            data-testid="refund-open"
            data-order-no={order.orderNo}
            disabled={order.refundStatus === 'REFUNDED'}
            onClick={() => onRefund(order)}
          >
            退款
          </Button>
        )}
      </header>

      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">商品名称</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">货架</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">数量</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">单价</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">优惠额</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">小计</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">已退</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">退款状态</th>
            <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-bold">类型</th>
          </tr>
        </thead>
        <tbody>
          {order.lines.map((line) => (
            <tr key={line.orderLineId} data-testid="ledger-line-row" data-line-id={line.orderLineId}>
              <td className="border-b border-gray-200 px-4 py-2 text-sm">{line.productName}</td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-shelf">
                {line.shelfCode ?? '-'}
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-quantity">
                {line.quantity} 件
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm">
                {isDiscounted(line) && (
                  <s className="mr-1 text-gray-500" data-testid="ledger-line-original-price">
                    {formatServerYuan(line.originalPrice) ?? PRICE_UNAVAILABLE_TEXT}
                  </s>
                )}
                <span data-testid="ledger-line-unit-price">
                  {formatServerYuan(line.unitPrice) ?? PRICE_UNAVAILABLE_TEXT}
                </span>
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-discount">
                {line.discountCents > 0 ? formatYuan(-line.discountCents) : '-'}
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-total">
                {formatServerYuan(line.totalPrice) ?? AMOUNT_UNAVAILABLE_TEXT}
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-refunded-qty">
                {line.refundedQty} 件
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-refund-status">
                {/* 行级文案的**唯一**实现（契约 §13.8）：不许在这里写 `refundedQty === quantity` */}
                {refundLineStatusLabel(line.refundedQty, line.quantity, line.shortQty)}
              </td>
              <td className="border-b border-gray-200 px-4 py-2 text-sm" data-testid="ledger-line-type">
                {LINE_SOURCE_LABELS[line.lineSource]}
                {line.schemeTitle !== null ? ` · ${line.schemeTitle}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center gap-2 border-t-2 border-black px-4 py-3">
        <label className="text-xs font-bold" htmlFor={`ledger-note-${order.orderId}`}>
          订单备注
        </label>
        <input
          id={`ledger-note-${order.orderId}`}
          data-testid="ledger-note-input"
          maxLength={NOTE_MAX_LENGTH}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          className="h-6 w-40 rounded border-2 border-black px-1 text-xs"
        />
        <Button size="sm" variant="secondary" onClick={() => void saveNotes()} disabled={saving}>
          {saving ? '保存中…' : '保存备注'}
        </Button>
        {noteNotice !== null && (
          <span className="text-sm text-gray-600" data-testid="ledger-note-notice">
            {noteNotice}
          </span>
        )}
        {noteError !== null && (
          <span className="text-sm text-red-600" data-testid="ledger-note-error">
            {noteError}
          </span>
        )}
      </div>
    </section>
  );
}

interface Props {
  orders: LedgerOrderItem[];
  /** 无 `ledger:refund` 权限时不传 ⇒ 不渲染退款入口（真正的拦截仍在服务端） */
  onRefund?: (order: LedgerOrderItem) => void;
  /** 备注保存成功后回写列表（数据由页面持有） */
  onNotesSaved?: (orderId: string, notes: string) => void;
}

/**
 * 订单分组表（契约 §13.2；`07-roadmap.md` §1.7：「有优惠的行显示原价划价与优惠额」）。
 *
 * 空态由页面负责（`暂无账单记录`）：组件只负责「有订单时怎么画」，
 * 两处都写空态会出现两种说法。
 */
export default function OrderLedgerTable({ orders, onRefund, onNotesSaved }: Props) {
  return (
    <div>
      {orders.map((order) => (
        <OrderGroup
          key={order.orderId}
          order={order}
          onRefund={onRefund}
          onNotesSaved={onNotesSaved}
        />
      ))}
    </div>
  );
}
